import {List, ListItem} from "@mui/material";
import React, {ReactNode} from "react";
import {Link} from "../../../bounce-ui";

export enum TermsType {
  PAYMENT = "payment",
  SHORT_PAYMENT = "short-payment",
  SINGLE_PAYMENT = "single-payment",
}

type TermsAndConditionsMapFn = (
  termsAndConditionsHandler: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void,
  partnerName?: string
) => React.ReactNode;

const DEFAULT_PARTNER_NAME = "Bounce AI";

const thirdPartyTermsContent: TermsAndConditionsMapFn = (termsAndConditionsHandler) => (
  <>
    I agree to the{" "}
    <Link size={12} href="#" onClick={termsAndConditionsHandler}>
      Terms & Conditions
    </Link>{" "}
    and authorize {DEFAULT_PARTNER_NAME} to initiate payment from my account as described above per my plan and
    designated payment preferences. I also agree to receive prerecorded or artificial voice calls and messages, which
    may incur carrier fees.
  </>
);

export const termsAndConditionsMap: Record<string, TermsAndConditionsMapFn> = {
  [TermsType.PAYMENT]: (termsAndConditionsHandler, fpPartnerName) => {
    const partnerName = fpPartnerName || DEFAULT_PARTNER_NAME;
    if (partnerName === DEFAULT_PARTNER_NAME) return thirdPartyTermsContent(termsAndConditionsHandler);
    return (
      <>
        By checking this box, I agree to the{" "}
        <Link size={12} href="#" onClick={termsAndConditionsHandler}>
          Terms & Conditions
        </Link>{" "}
        authorize {partnerName} to process payments per my plan, and send confirmations, reminders, and receipts. I may
        update preferences or cancel authorization before a scheduled payment by notifying {partnerName} online or by
        phone.
      </>
    );
  },
  [TermsType.SHORT_PAYMENT]: (termsAndConditionsHandler, fpPartnerName) => {
    const partnerName = fpPartnerName || DEFAULT_PARTNER_NAME;
    if (partnerName === DEFAULT_PARTNER_NAME) return thirdPartyTermsContent(termsAndConditionsHandler);
    return (
      <>
        I agree to the{" "}
        <Link size={12} href="#" onClick={termsAndConditionsHandler}>
          Terms & Conditions
        </Link>{" "}
        and authorize {partnerName} to initiate payment from my account as described above in accordance with my plan
        and pursuant to my designated payment preferences.
      </>
    );
  },
  [TermsType.SINGLE_PAYMENT]: (termsAndConditionsHandler, fpPartnerName) => {
    const partnerName = fpPartnerName || DEFAULT_PARTNER_NAME;
    if (partnerName === DEFAULT_PARTNER_NAME) return thirdPartyTermsContent(termsAndConditionsHandler);
    return (
      <>
        By checking this box, you confirm that you have read and agree to the{" "}
        <Link size={12} href="#" onClick={termsAndConditionsHandler}>
          Terms & Conditions
        </Link>{" "}
        and authorize {partnerName} to initiate a payment as described above and pursuant to your designated payment
        preferences, and to email confirmations, reminders, and receipts.
      </>
    );
  },
};

type BulletListProps = {
  children: ReactNode;
  gap?: number | string;
  paddingLeft?: number | string;
  dense?: boolean;
};

export const BulletList = ({children, gap = "8px", paddingLeft = "16px", dense = true}: BulletListProps) => {
  return (
    <List component={"ul"} sx={{listStyleType: "disc", pl: paddingLeft, gap, ...(dense && {mt: 0, mb: 0})}}>
      {children}
    </List>
  );
};

type BulletListItemProps = {
  children: ReactNode;
  lineHeight?: number;
};

export const BulletListItem = ({children, lineHeight = 1.4}: BulletListItemProps) => {
  return (
    <ListItem component={"li"} sx={{display: "list-item", p: 0, m: 0, lineHeight}}>
      {children}
    </ListItem>
  );
};
